import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Shield, Briefcase, UserCheck, ArrowLeft, RefreshCw,
  Eye, LogIn, Activity, BarChart3, Clock, Mail, Phone,
  ChevronRight, Search, Filter, ToggleLeft, ToggleRight,
  Building2, TrendingUp, Globe, ExternalLink, UserX, CheckCircle,
  AlertCircle, PieChart, Calendar, Edit, Trash2, X, Check,
  Download, Send, MoreVertical, ChevronDown, Image, History,
  MapPin, Monitor, Plus, UserPlus, Key
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple Pie Chart Component
function SimplePieChart({ data }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulativePercent = 0;
  
  const getCoordinates = (percent) => {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  };
  
  return (
    <div className="flex items-center justify-center gap-6">
      <svg viewBox="-1.1 -1.1 2.2 2.2" className="w-32 h-32">
        {data.map((slice, idx) => {
          const [startX, startY] = getCoordinates(cumulativePercent);
          const percent = slice.value / total;
          cumulativePercent += percent;
          const [endX, endY] = getCoordinates(cumulativePercent);
          const largeArc = percent > 0.5 ? 1 : 0;
          
          const pathData = [
            `M ${startX} ${startY}`,
            `A 1 1 0 ${largeArc} 1 ${endX} ${endY}`,
            'L 0 0',
          ].join(' ');
          
          return (
            <path key={idx} d={pathData} fill={slice.color} />
          );
        })}
      </svg>
      <div className="space-y-2">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-sm text-gray-600">{item.label}</span>
            <span className="text-sm font-bold">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, subtitle, icon: Icon, color, onClick, trend }) {
  return (
    <div 
      onClick={onClick}
      className={`bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-all ${onClick ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[#AEAEB2] mb-1">{title}</p>
          <p className={`text-3xl font-bold ${color || 'text-gray-900'}`}>{value}</p>
          {subtitle && (
            <p className="text-xs text-[#AEAEB2] mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color?.replace('text-', 'bg-').replace('600', '100') || 'bg-gray-100'}`}>
          <Icon className={`w-6 h-6 ${color || 'text-gray-600'}`} />
        </div>
      </div>
      {trend && (
        <div className={`mt-2 text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend > 0 ? '+' : ''}{trend}% vs semana anterior
        </div>
      )}
    </div>
  );
}

// User Row Component with enhanced features
function UserRow({ user, type, onImpersonate, onToggleActive, onEdit, onDelete, onViewDetails, selected, onSelect, onResetPin }) {
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  
  const handleImpersonate = async () => {
    setLoading(true);
    await onImpersonate(user, type);
    setLoading(false);
  };
  
  const getUserId = () => {
    if (type === 'admin') return user.admin_id;
    if (type === 'worker') return user.worker_id;
    if (type === 'client') return user.client_id;
    return user.id;
  };
  
  const isActive = type === 'admin' ? true : user.is_active !== false;
  const photoUrl = user.photo_url || user.avatar_url || user.profile_image;
  
  return (
    <div className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${!isActive ? 'opacity-60' : ''} ${selected ? 'bg-purple-50' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Checkbox for bulk selection */}
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(getUserId())}
            className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
          />
          
          {/* Profile Photo or Icon */}
          {photoUrl ? (
            <img 
              src={photoUrl} 
              alt={user.name} 
              className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
            />
          ) : (
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              type === 'admin' ? 'bg-red-100' :
              type === 'worker' ? 'bg-blue-100' :
              'bg-green-100'
            }`}>
              {type === 'admin' && <Shield className="w-5 h-5 text-red-600" />}
              {type === 'worker' && <Briefcase className="w-5 h-5 text-blue-600" />}
              {type === 'client' && <Building2 className="w-5 h-5 text-green-600" />}
            </div>
          )}
          
          <div>
            <p className="font-medium text-gray-900">{user.name || 'Sin nombre'}</p>
            <p className="text-sm text-[#AEAEB2]">{user.email || user.authorized_emails?.[0] || 'Sin email'}</p>
          </div>
          
          {/* Type Badge */}
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            type === 'admin' ? 'bg-red-100 text-red-700' :
            type === 'worker' ? 'bg-blue-100 text-blue-700' :
            'bg-green-100 text-green-700'
          }`}>
            {type === 'admin' ? 'Admin' : type === 'worker' ? 'Trabajador' : 'Cliente'}
          </span>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Last Activity */}
          {user.last_activity && (
            <div className="text-right hidden lg:block">
              <p className="text-xs text-[#AEAEB2]">Última actividad</p>
              <p className="text-sm text-gray-600 truncate max-w-[150px]">
                {user.last_activity.page}
              </p>
              <p className="text-xs text-[#AEAEB2]">
                {new Date(user.last_activity.timestamp).toLocaleDateString('es-MX', {
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                })}
              </p>
            </div>
          )}
          
          {/* Status Badge */}
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            isActive 
              ? 'bg-green-100 text-green-700' 
              : 'bg-gray-100 text-[#AEAEB2]'
          }`}>
            {isActive ? 'Activo' : 'Inactivo'}
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1">
            {/* View Details */}
            <button
              onClick={() => onViewDetails(user, type)}
              className="p-2 rounded-lg hover:bg-gray-100 text-[#AEAEB2] hover:text-gray-600 transition-colors"
              title="Ver detalles"
            >
              <Eye className="w-4 h-4" />
            </button>
            
            {/* Impersonate */}
            <button
              onClick={handleImpersonate}
              disabled={loading || !isActive}
              className="flex items-center gap-1 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm disabled:opacity-50"
              title="Acceder como este usuario"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Entrar</span>
            </button>
            
            {/* More Actions Menu */}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 rounded-lg hover:bg-gray-100 text-[#AEAEB2] hover:text-gray-600 transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              
              {showMenu && (
                <>
                  <div className="fixed inset-0" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => { onEdit(user, type); setShowMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Editar usuario
                    </button>
                    {type !== 'client' && (
                      <button
                        onClick={() => { onResetPin(user); setShowMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-orange-50 text-orange-700 flex items-center gap-2"
                        data-testid="reset-pin-btn"
                      >
                        <Key className="w-4 h-4" />
                        Resetear PIN
                      </button>
                    )}
                    {type !== 'admin' && (
                      <>
                        <button
                          onClick={() => { onToggleActive(getUserId(), type); setShowMenu(false); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          {isActive ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          {isActive ? 'Desactivar' : 'Activar'}
                        </button>
                        <hr className="my-1" />
                        <button
                          onClick={() => { onDelete(user, type); setShowMenu(false); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Eliminar usuario
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// User Detail Modal
function UserDetailModal({ user, type, activities, onClose }) {
  if (!user) return null;
  
  const photoUrl = user.photo_url || user.avatar_url || user.profile_image;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {photoUrl ? (
                <img src={photoUrl} alt={user.name} className="w-16 h-16 rounded-full object-cover border-4 border-white/30" />
              ) : (
                <div className={`w-16 h-16 rounded-full flex items-center justify-center border-4 border-white/30 ${
                  type === 'admin' ? 'bg-red-500' : type === 'worker' ? 'bg-blue-500' : 'bg-green-500'
                }`}>
                  {type === 'admin' && <Shield className="w-8 h-8 text-white" />}
                  {type === 'worker' && <Briefcase className="w-8 h-8 text-white" />}
                  {type === 'client' && <Building2 className="w-8 h-8 text-white" />}
                </div>
              )}
              <div>
                <h3 className="text-xl font-bold">{user.name || 'Sin nombre'}</h3>
                <p className="text-white/80">{user.email || user.authorized_emails?.[0]}</p>
                <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs ${
                  type === 'admin' ? 'bg-red-400/30' : type === 'worker' ? 'bg-blue-400/30' : 'bg-green-400/30'
                }`}>
                  {type === 'admin' ? 'Administrador' : type === 'worker' ? 'Trabajador' : 'Cliente'}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid md:grid-cols-2 gap-6">
            {/* User Info */}
            <div className="space-y-4">
              <h4 className="font-bold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                Información del Usuario
              </h4>
              
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-[#AEAEB2]">ID:</span>
                  <span className="font-mono text-sm">{user.admin_id || user.worker_id || user.client_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#AEAEB2]">Email:</span>
                  <span>{user.email || user.authorized_emails?.[0]}</span>
                </div>
                {user.phone && (
                  <div className="flex justify-between">
                    <span className="text-[#AEAEB2]">Teléfono:</span>
                    <span>{user.phone}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#AEAEB2]">Estado:</span>
                  <span className={user.is_active !== false ? 'text-green-600' : 'text-red-600'}>
                    {user.is_active !== false ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                {user.created_at && (
                  <div className="flex justify-between">
                    <span className="text-[#AEAEB2]">Registro:</span>
                    <span>{new Date(user.created_at).toLocaleDateString('es-MX')}</span>
                  </div>
                )}
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{activities.length}</p>
                  <p className="text-xs text-[#AEAEB2]">Páginas visitadas</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {user.last_activity 
                      ? Math.floor((Date.now() - new Date(user.last_activity.timestamp)) / (1000 * 60 * 60))
                      : '-'
                    }h
                  </p>
                  <p className="text-xs text-[#AEAEB2]">Última actividad</p>
                </div>
              </div>
            </div>
            
            {/* Activity History */}
            <div className="space-y-4">
              <h4 className="font-bold text-gray-900 flex items-center gap-2">
                <History className="w-5 h-5 text-purple-500" />
                Historial de Actividad
              </h4>
              
              <div className="bg-gray-50 rounded-xl p-4 max-h-[300px] overflow-y-auto">
                {activities.length > 0 ? (
                  <div className="space-y-2">
                    {activities.map((activity, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-white rounded-lg">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <Globe className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{activity.page}</p>
                          <p className="text-xs text-[#AEAEB2]">
                            {new Date(activity.timestamp).toLocaleString('es-MX')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#AEAEB2] py-6">Sin actividad registrada</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Edit User Modal
function EditUserModal({ user, type, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || user?.authorized_emails?.[0] || '',
    phone: user?.phone || '',
    is_active: user?.is_active !== false
  });
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };
  
  if (!user) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold">Editar Usuario</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500"
              disabled={type === 'admin'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          {type !== 'admin' && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <span className="text-sm font-medium text-gray-700">Usuario activo</span>
              <button
                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  formData.is_active ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  formData.is_active ? 'right-1' : 'left-1'
                }`} />
              </button>
            </div>
          )}
        </div>
        
        <div className="p-6 border-t flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-xl hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Bulk Actions Bar
function BulkActionsBar({ selectedCount, onDeactivate, onActivate, onEmail, onExport, onClearSelection }) {
  if (selectedCount === 0) return null;
  
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-4 z-40" data-testid="bulk-actions-bar">
      <span className="text-sm">
        <strong>{selectedCount}</strong> seleccionados
      </span>
      <div className="w-px h-6 bg-gray-700" />
      <button
        onClick={onActivate}
        className="flex items-center gap-2 px-3 py-1.5 bg-green-600 rounded-lg hover:bg-green-700 text-sm"
        data-testid="bulk-activate-btn"
      >
        <ToggleRight className="w-4 h-4" />
        Activar
      </button>
      <button
        onClick={onDeactivate}
        className="flex items-center gap-2 px-3 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 text-sm"
        data-testid="bulk-deactivate-btn"
      >
        <ToggleLeft className="w-4 h-4" />
        Desactivar
      </button>
      <button
        onClick={onEmail}
        className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 rounded-lg hover:bg-blue-700 text-sm"
        data-testid="bulk-email-btn"
      >
        <Mail className="w-4 h-4" />
        Enviar Email
      </button>
      <button
        onClick={onExport}
        className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 rounded-lg hover:bg-purple-700 text-sm"
        data-testid="bulk-export-btn"
      >
        <Download className="w-4 h-4" />
        Exportar CSV
      </button>
      <button
        onClick={onClearSelection}
        className="p-1.5 hover:bg-white/10 rounded-lg"
        data-testid="bulk-clear-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// Impersonation Banner (shown when admin is viewing as another user)
export function ImpersonationBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [userName, setUserName] = useState('');
  
  useEffect(() => {
    // Check localStorage only on client side
    if (typeof window !== 'undefined') {
      const impersonatedBy = localStorage.getItem('impersonated_by');
      if (impersonatedBy) {
        try {
          const currentUser = JSON.parse(localStorage.getItem('portal_user') || '{}');
          setUserName(currentUser.name || currentUser.email || 'Usuario');
          setShowBanner(true);
        } catch (e) {
          setShowBanner(false);
        }
      }
    }
  }, []);
  
  if (!showBanner) return null;
  
  const handleReturnToAdmin = () => {
    localStorage.removeItem('impersonated_by');
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    window.location.href = '/admin/users';
  };
  
  return (
    <div className="fixed top-0 left-0 right-0 bg-amber-500 text-black px-4 py-2 z-50 flex items-center justify-center gap-4">
      <Eye className="w-5 h-5" />
      <span className="font-medium">
        Estás viendo como <strong>{userName}</strong>
      </span>
      <button
        onClick={handleReturnToAdmin}
        className="px-3 py-1 bg-black text-white rounded-lg text-sm hover:bg-white/70"
      >
        Volver a Admin
      </button>
    </div>
  );
}

export default function AdminUserManagementPage({ embedded = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({});
  const [dashboardStats, setDashboardStats] = useState({});
  const [admins, setAdmins] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [clients, setClients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [pageAnalytics, setPageAnalytics] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [activityFilter, setActivityFilter] = useState('all'); // all, today, week, month
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [detailUser, setDetailUser] = useState(null);
  const [detailType, setDetailType] = useState(null);
  const [userActivities, setUserActivities] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [editType, setEditType] = useState(null);
  const [newUsersThisWeek, setNewUsersThisWeek] = useState(0);
  
  const token = localStorage.getItem('portal_token') || localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const [statsRes, dashRes, activityRes, pagesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/users/dashboard-stats`, { headers }),
        axios.get(`${API_URL}/api/admin/users/activity?limit=50`, { headers }),
        axios.get(`${API_URL}/api/admin/users/analytics/pages`, { headers })
      ]);
      
      setStats(statsRes.data);
      setDashboardStats(dashRes.data);
      setActivities(activityRes.data.activities || []);
      setPageAnalytics(pagesRes.data.pages || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async (type) => {
    try {
      let newCount = 0;
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      if (type === 'admins' || type === 'all') {
        const res = await axios.get(`${API_URL}/api/admin/users/admins`, { headers });
        setAdmins(res.data.admins || []);
      }
      if (type === 'workers' || type === 'all') {
        const res = await axios.get(`${API_URL}/api/admin/users/workers`, { headers });
        const workersList = res.data.workers || [];
        setWorkers(workersList);
        newCount += workersList.filter(w => w.created_at && new Date(w.created_at) > weekAgo).length;
      }
      if (type === 'clients' || type === 'all') {
        const res = await axios.get(`${API_URL}/api/admin/users/clients`, { headers });
        const clientsList = res.data.clients || [];
        setClients(clientsList);
        newCount += clientsList.filter(c => c.created_at && new Date(c.created_at) > weekAgo).length;
      }
      
      setNewUsersThisWeek(newCount);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadUsers('all');
  }, [loadData, loadUsers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await loadUsers('all');
    setRefreshing(false);
    toast.success('Datos actualizados');
  };

  const handleImpersonate = async (user, type) => {
    try {
      const userId = type === 'admin' ? user.admin_id :
                     type === 'worker' ? user.worker_id :
                     user.client_id;
      
      const res = await axios.post(
        `${API_URL}/api/admin/users/impersonate`,
        { user_type: type, user_id: userId },
        { headers }
      );
      
      if (res.data.success) {
        // Store impersonation metadata (NOT the JWT — that lives in the cookie
        // already set by the backend impersonate endpoint).
        const currentAdminEmail = JSON.parse(localStorage.getItem('portal_user') || '{}').email;
        localStorage.setItem('impersonated_by', currentAdminEmail);
        localStorage.setItem('portal_user', JSON.stringify(res.data.user));
        
        toast.success(`Accediendo como ${res.data.user.name}`);
        
        // Navigate to user's dashboard
        window.location.href = res.data.redirect_url;
      }
    } catch (error) {
      console.error('Error impersonating:', error);
      toast.error(error.response?.data?.detail || 'Error al acceder');
    }
  };

  const handleToggleActive = async (userId, type) => {
    try {
      const endpoint = type === 'worker' 
        ? `/api/admin/users/workers/${userId}/toggle-active`
        : `/api/admin/users/clients/${userId}/toggle-active`;
      
      await axios.patch(`${API_URL}${endpoint}`, {}, { headers });
      
      toast.success('Estado actualizado');
      loadUsers(type === 'worker' ? 'workers' : 'clients');
    } catch (error) {
      console.error('Error toggling active:', error);
      toast.error('Error al cambiar estado');
    }
  };

  const handleViewDetails = async (user, type) => {
    setDetailUser(user);
    setDetailType(type);
    
    // Load user's activities
    try {
      const email = user.email || user.authorized_emails?.[0];
      const res = await axios.get(`${API_URL}/api/admin/users/activity?limit=50`, { headers });
      const userActs = (res.data.activities || []).filter(a => a.email === email);
      setUserActivities(userActs);
    } catch (error) {
      console.error('Error loading user activities:', error);
      setUserActivities([]);
    }
  };

  const handleEdit = (user, type) => {
    setEditUser(user);
    setEditType(type);
  };

  const handleSaveEdit = async (formData) => {
    try {
      const userId = editType === 'admin' ? editUser.admin_id :
                     editType === 'worker' ? editUser.worker_id :
                     editUser.client_id;
      
      const endpoint = editType === 'worker' 
        ? `/api/workers/${userId}`
        : editType === 'client'
        ? `/api/clients/${userId}`
        : null;
      
      if (endpoint) {
        await axios.put(`${API_URL}${endpoint}`, formData, { headers });
        toast.success('Usuario actualizado');
        loadUsers('all');
      }
      
      setEditUser(null);
      setEditType(null);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (user, type) => {
    if (!confirm(`¿Estás seguro de eliminar a ${user.name || 'este usuario'}?`)) return;
    
    try {
      const userId = type === 'worker' ? user.worker_id : user.client_id;
      const endpoint = type === 'worker' 
        ? `/api/workers/${userId}`
        : `/api/clients/${userId}`;
      
      await axios.delete(`${API_URL}${endpoint}`, { headers });
      toast.success('Usuario eliminado');
      loadUsers('all');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleResetPin = async (user) => {
    const email = user.email || user.authorized_emails?.[0];
    if (!email) { toast.error('Usuario sin email'); return; }
    if (!confirm(`¿Resetear el PIN de ${user.name || email}? Esto cerrará todas sus sesiones activas.`)) return;
    try {
      await axios.post(`${API_URL}/api/auth/reset-pin`, { user_email: email }, { headers });
      toast.success(`PIN reseteado y sesiones cerradas para ${user.name || email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al resetear PIN');
    }
  };

  const handleSelectAll = (users, type) => {
    const userIds = users.map(u => type === 'admin' ? u.admin_id : type === 'worker' ? u.worker_id : u.client_id);
    const allSelected = userIds.every(id => selectedUsers.includes(id));
    
    if (allSelected) {
      setSelectedUsers(prev => prev.filter(id => !userIds.includes(id)));
    } else {
      setSelectedUsers(prev => [...new Set([...prev, ...userIds])]);
    }
  };

  const handleBulkActivate = async () => {
    if (!selectedUsers.length) return;
    const workerIds = workers.filter(w => selectedUsers.includes(w.worker_id)).map(w => w.worker_id);
    const clientIds = clients.filter(c => selectedUsers.includes(c.client_id)).map(c => c.client_id);
    try {
      const promises = [];
      if (workerIds.length) {
        promises.push(axios.post(`${API_URL}/api/admin/users/bulk-toggle`, { user_ids: workerIds, user_type: 'worker', activate: true }, { headers }));
      }
      if (clientIds.length) {
        promises.push(axios.post(`${API_URL}/api/admin/users/bulk-toggle`, { user_ids: clientIds, user_type: 'client', activate: true }, { headers }));
      }
      const results = await Promise.all(promises);
      const total = results.reduce((s, r) => s + (r.data.modified_count || 0), 0);
      toast.success(`${total} usuarios activados`);
      setSelectedUsers([]);
      loadUsers('all');
    } catch (error) {
      toast.error('Error al activar usuarios');
    }
  };

  const handleBulkDeactivate = async () => {
    if (!selectedUsers.length) return;
    if (!confirm(`¿Desactivar ${selectedUsers.length} usuarios seleccionados?`)) return;
    const workerIds = workers.filter(w => selectedUsers.includes(w.worker_id)).map(w => w.worker_id);
    const clientIds = clients.filter(c => selectedUsers.includes(c.client_id)).map(c => c.client_id);
    try {
      const promises = [];
      if (workerIds.length) {
        promises.push(axios.post(`${API_URL}/api/admin/users/bulk-toggle`, { user_ids: workerIds, user_type: 'worker', activate: false }, { headers }));
      }
      if (clientIds.length) {
        promises.push(axios.post(`${API_URL}/api/admin/users/bulk-toggle`, { user_ids: clientIds, user_type: 'client', activate: false }, { headers }));
      }
      const results = await Promise.all(promises);
      const total = results.reduce((s, r) => s + (r.data.modified_count || 0), 0);
      toast.success(`${total} usuarios desactivados`);
      setSelectedUsers([]);
      loadUsers('all');
    } catch (error) {
      toast.error('Error al desactivar usuarios');
    }
  };

  const handleBulkEmail = () => {
    toast.info('Función de email masivo próximamente');
  };

  const handleExportCSV = () => {
    const allUsers = [
      ...admins.map(u => ({ ...u, type: 'admin' })),
      ...workers.map(u => ({ ...u, type: 'worker' })),
      ...clients.map(u => ({ ...u, type: 'client' }))
    ];
    
    const selectedData = allUsers.filter(u => {
      const id = u.admin_id || u.worker_id || u.client_id;
      return selectedUsers.includes(id);
    });
    
    const csvContent = [
      ['Tipo', 'Nombre', 'Email', 'Estado', 'Última Actividad'].join(','),
      ...selectedData.map(u => [
        u.type,
        u.name || '',
        u.email || u.authorized_emails?.[0] || '',
        u.is_active !== false ? 'Activo' : 'Inactivo',
        u.last_activity?.timestamp || ''
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usuarios_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('CSV exportado');
    setSelectedUsers([]);
  };

  const filterUsers = (users, type) => {
    let filtered = users;
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user => {
        const name = (user.name || '').toLowerCase();
        const email = (user.email || user.authorized_emails?.[0] || '').toLowerCase();
        return name.includes(term) || email.includes(term);
      });
    }
    
    // Activity filter
    if (activityFilter !== 'all') {
      const now = new Date();
      let cutoff = new Date();
      
      if (activityFilter === 'today') cutoff.setHours(0, 0, 0, 0);
      else if (activityFilter === 'week') cutoff.setDate(now.getDate() - 7);
      else if (activityFilter === 'month') cutoff.setMonth(now.getMonth() - 1);
      
      filtered = filtered.filter(user => {
        if (!user.last_activity?.timestamp) return false;
        return new Date(user.last_activity.timestamp) > cutoff;
      });
    }
    
    return filtered;
  };

  // Pie chart data
  const pieData = [
    { label: 'Admins', value: stats.admins || 0, color: '#ef4444' },
    { label: 'Trabajadores', value: stats.workers || 0, color: '#3b82f6' },
    { label: 'Clientes', value: stats.clients || 0, color: '#22c55e' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#AEAEB2]">Cargando panel de usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Users className="w-7 h-7" />
                  Panel de Usuarios
                </h1>
                <p className="text-white/80 text-sm">
                  Gestión completa y acceso directo a todos los usuarios
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 text-sm"
              >
                <Download className="w-4 h-4" />
                Exportar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <StatCard
            title="Total Usuarios"
            value={stats.total_users || 0}
            icon={Users}
            color="text-purple-600"
          />
          <StatCard
            title="Administradores"
            value={stats.admins || 0}
            icon={Shield}
            color="text-red-600"
            onClick={() => setActiveTab('admins')}
          />
          <StatCard
            title="Trabajadores"
            value={stats.workers || 0}
            icon={Briefcase}
            color="text-blue-600"
            onClick={() => setActiveTab('workers')}
          />
          <StatCard
            title="Clientes"
            value={stats.clients || 0}
            icon={Building2}
            color="text-green-600"
            onClick={() => setActiveTab('clients')}
          />
          <StatCard
            title="Activos Hoy"
            value={dashboardStats.today_active_users || 0}
            subtitle={`${dashboardStats.week_active_users || 0} esta semana`}
            icon={Activity}
            color="text-amber-600"
          />
          <StatCard
            title="Nuevos"
            value={newUsersThisWeek}
            subtitle="esta semana"
            icon={UserPlus}
            color="text-cyan-600"
          />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-4">
          {[
            { id: 'overview', label: 'Resumen', icon: PieChart },
            { id: 'admins', label: `Admins (${admins.length})`, icon: Shield },
            { id: 'workers', label: `Trabajadores (${workers.length})`, icon: Briefcase },
            { id: 'clients', label: `Clientes (${clients.length})`, icon: Building2 },
            { id: 'activity', label: 'Actividad', icon: Activity }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        {['admins', 'workers', 'clients'].includes(activeTab) && (
          <div className="mb-6 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#AEAEB2]" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre o email..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <select
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Todos los usuarios</option>
              <option value="today">Activos hoy</option>
              <option value="week">Activos esta semana</option>
              <option value="month">Activos este mes</option>
            </select>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Distribution Chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-6">
                <PieChart className="w-5 h-5 text-purple-500" />
                Distribución de Usuarios
              </h3>
              <SimplePieChart data={pieData} />
            </div>

            {/* Top Pages */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-purple-500" />
                Páginas más visitadas
              </h3>
              <div className="space-y-3">
                {pageAnalytics.slice(0, 6).map((page, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-sm text-gray-700 truncate max-w-[200px]">{page.page}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-[#AEAEB2]">{page.unique_users} usuarios</span>
                      <span className="text-sm font-bold text-purple-600">{page.visits}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-amber-500" />
                Actividad Reciente
              </h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {activities.slice(0, 10).map((activity, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      activity.user_type === 'admin' ? 'bg-red-100 text-red-600' :
                      activity.user_type === 'worker' ? 'bg-blue-100 text-blue-600' :
                      'bg-green-100 text-green-600'
                    }`}>
                      {activity.user_type}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{activity.email}</p>
                      <p className="text-xs text-[#AEAEB2]">{activity.page}</p>
                    </div>
                    <p className="text-xs text-[#AEAEB2]">
                      {new Date(activity.timestamp).toLocaleTimeString('es-MX', {
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Active by Type */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                Usuarios Activos Hoy
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <Shield className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-600">
                    {dashboardStats.active_by_type?.admin || 0}
                  </p>
                  <p className="text-sm text-[#AEAEB2]">Admins</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <Briefcase className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-600">
                    {dashboardStats.active_by_type?.worker || 0}
                  </p>
                  <p className="text-sm text-[#AEAEB2]">Trabajadores</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <Building2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-600">
                    {dashboardStats.active_by_type?.client || 0}
                  </p>
                  <p className="text-sm text-[#AEAEB2]">Clientes</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'admins' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-red-50 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                Administradores
                <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                  {filterUsers(admins, 'admin').length}
                </span>
              </h3>
              <button
                onClick={() => handleSelectAll(filterUsers(admins, 'admin'), 'admin')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Seleccionar todos
              </button>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {filterUsers(admins, 'admin').map((admin, idx) => (
                <UserRow
                  key={admin.admin_id || idx}
                  user={admin}
                  type="admin"
                  onImpersonate={handleImpersonate}
                  onToggleActive={handleToggleActive}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onViewDetails={handleViewDetails}
                  selected={selectedUsers.includes(admin.admin_id)}
                  onSelect={() => handleSelectUser(admin.admin_id)}
                  onResetPin={handleResetPin}
                />
              ))}
              {filterUsers(admins, 'admin').length === 0 && (
                <p className="text-center text-[#AEAEB2] py-12">No hay administradores</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'workers' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-blue-50 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-500" />
                Trabajadores
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {filterUsers(workers, 'worker').length}
                </span>
              </h3>
              <button
                onClick={() => handleSelectAll(filterUsers(workers, 'worker'), 'worker')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Seleccionar todos
              </button>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {filterUsers(workers, 'worker').map((worker, idx) => (
                <UserRow
                  key={worker.worker_id || idx}
                  user={worker}
                  type="worker"
                  onImpersonate={handleImpersonate}
                  onToggleActive={handleToggleActive}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onViewDetails={handleViewDetails}
                  selected={selectedUsers.includes(worker.worker_id)}
                  onSelect={() => handleSelectUser(worker.worker_id)}
                  onResetPin={handleResetPin}
                />
              ))}
              {filterUsers(workers, 'worker').length === 0 && (
                <p className="text-center text-[#AEAEB2] py-12">No hay trabajadores</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'clients' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-green-50 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-green-500" />
                Clientes
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                  {filterUsers(clients, 'client').length}
                </span>
              </h3>
              <button
                onClick={() => handleSelectAll(filterUsers(clients, 'client'), 'client')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Seleccionar todos
              </button>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {filterUsers(clients, 'client').map((client, idx) => (
                <UserRow
                  key={client.client_id || idx}
                  user={client}
                  type="client"
                  onImpersonate={handleImpersonate}
                  onToggleActive={handleToggleActive}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onViewDetails={handleViewDetails}
                  selected={selectedUsers.includes(client.client_id)}
                  onSelect={() => handleSelectUser(client.client_id)}
                  onResetPin={handleResetPin}
                />
              ))}
              {filterUsers(clients, 'client').length === 0 && (
                <p className="text-center text-[#AEAEB2] py-12">No hay clientes</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-500" />
                Historial de Actividad Completo
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {activities.map((activity, idx) => (
                <div key={idx} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        activity.user_type === 'admin' ? 'bg-red-100 text-red-600' :
                        activity.user_type === 'worker' ? 'bg-blue-100 text-blue-600' :
                        'bg-green-100 text-green-600'
                      }`}>
                        {activity.user_type}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{activity.email}</p>
                        <p className="text-sm text-[#AEAEB2]">Visitó: {activity.page}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        {new Date(activity.timestamp).toLocaleDateString('es-MX')}
                      </p>
                      <p className="text-xs text-[#AEAEB2]">
                        {new Date(activity.timestamp).toLocaleTimeString('es-MX')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {activities.length === 0 && (
                <p className="text-center text-[#AEAEB2] py-12">Sin actividad registrada</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedUsers.length}
        onActivate={handleBulkActivate}
        onDeactivate={handleBulkDeactivate}
        onEmail={handleBulkEmail}
        onExport={handleExportCSV}
        onClearSelection={() => setSelectedUsers([])}
      />

      {/* User Detail Modal */}
      {detailUser && (
        <UserDetailModal
          user={detailUser}
          type={detailType}
          activities={userActivities}
          onClose={() => setDetailUser(null)}
        />
      )}

      {/* Edit User Modal */}
      {editUser && (
        <EditUserModal
          user={editUser}
          type={editType}
          onClose={() => setEditUser(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
}
